import API_CONSTANTS from '@/utils/apiConstants';
import { createApi } from '@reduxjs/toolkit/query/react';
import customBaseQuery from './customBaseQuery';

const baseUrl = process.env.NEXT_PUBLIC_API_URL.replace(/\/+$/, '');

export const brokerApi = createApi({
  reducerPath: 'brokerApi',
  baseQuery: customBaseQuery({ baseUrl }),
  tagTypes: ['Prospect', 'Documents', 'AssociatedFirm'],
  endpoints: (builder) => ({
    getDocumentTypes: builder.query({
      query: (type) => ({
        url: `${API_CONSTANTS.GET_DOCUMENT_TYPES}/${type}`,
        method: 'GET',
      }),
    }),
    getDocuments: builder.query({
      query: ({ brokerId, search, page = 0, size = 10 } = {}) => ({
        url: API_CONSTANTS.BROKER.GET_DOCUMENTS.replace(':id', brokerId),
        method: 'GET',
        params: {
          page,
          size,
          search: search?.trim() || undefined,
        },
      }),
      providesTags: ['Documents'],
    }),
    getBrokers: builder.query({
      query: ({ page = 0, size = 10, search, sortBy, sortOrder, name, brokerId, salesAreaCity, type, specialization, dateJoinedFrom, dateJoinedTo, approvalStatus } = {}) => ({
        url: '/broker/all',
        method: 'GET',
        params: {
          page,
          size,
          search: search || undefined,
          sortBy,
          sortOrder,
          name: name || undefined,
          brokerId: brokerId || undefined,
          salesAreaCity: salesAreaCity || undefined,
          type: type || undefined,
          specialization: specialization || undefined,
          dateJoinedFrom: dateJoinedFrom || undefined,
          dateJoinedTo: dateJoinedTo || undefined,
          approvalStatus: approvalStatus || undefined,
        },
      }),
    }),
    createProspect: builder.mutation({
      query: (prospectData) => ({
        url: API_CONSTANTS.BROKER.ADD_BASIC,
        method: 'POST',
        body: prospectData,
      }),
      invalidatesTags: ['Prospect'],
    }),
    uploadProfileImage: builder.mutation({
      query: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: API_CONSTANTS.BROKER.UPLOAD_PROFILE_IMAGE,
          method: 'POST',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          transformRequest: [],
        };
      },
    }),
    createProspectLicense: builder.mutation({
      query: ({ brokerId, licenseData }) => {
        return {
          url: API_CONSTANTS.BROKER.CREATE_PROSPECT_LICENSE.replace(':id', brokerId),
          method: 'POST',
          body: licenseData,
        };
      },
      invalidatesTags: ['Prospect'],
    }),
    addDocument: builder.mutation({
      query: ({ brokerId, documentData }) => {
        return {
          url: API_CONSTANTS.BROKER.ADD_DOCUMENT.replace(':id', brokerId),
          method: 'POST',
          body: documentData,
        };
      },
      invalidatesTags: ['Documents'],
    }),
    getBrokerStats: builder.query({
      query: () => `${API_CONSTANTS.BROKER.GET_BROKER_STATS}`,
    }),
    getSampleDocuments: builder.query({
      query: (type) => `${API_CONSTANTS.GET_SAMPLE_DOCUMENTS}/${type}`,
    }),
    downloadSampleDocuments: builder.mutation({
      query: (documentNames) => ({
        url: `${API_CONSTANTS.GET_SAMPLE_DOCUMENTS}/download`,
        method: 'GET',
        params: { documentNames: documentNames.join(',') },
      }),
    }),
    deleteDocument: builder.mutation({
      query: ({ brokerId, documentType }) => ({
        url: API_CONSTANTS.BROKER.DELETE_DOCUMENT.replace(':brokerId', brokerId).replace(':documentType', documentType),
        method: 'DELETE',
      }),
      invalidatesTags: ['Documents'],
    }),
    getDocumentById: builder.query({
      query: ({ brokerId, documentId }) => `${API_CONSTANTS.BROKER.GET_DOCUMENTS}/${documentId}`.replace(':id', brokerId),
      providesTags: (result, error, { documentId }) => [{ type: 'Documents', id: documentId }],
    }),
    updateDocument: builder.mutation({
      query: ({ brokerId, documentId, documentData }) => ({
        url: API_CONSTANTS.BROKER.UPDATE_DOCUMENT.replace(':brokerId', brokerId).replace(':documentId', documentId),
        method: 'PUT',
        body: documentData,
      }),
      invalidatesTags: (result, error, { documentId }) => ['Documents', { type: 'Documents', id: documentId }],
    }),

    getBrokerDetails: builder.query({
      query: (id) => {
        if (id) {
          return API_CONSTANTS.BROKER.GET_BROKER_DETAILS.replace(':id', id);
        }
        return API_CONSTANTS.BROKER.GET_BROKER_DETAILS.replace('/:id', '/myProfile');
      },
      providesTags: ['Prospect'],
    }),
    getBrokerProfileCardDetails: builder.query({
      query: (id) => {
        // If ID is provided, use it in the URL
        if (id) {
          return API_CONSTANTS.BROKER.GET_BROKER_PROFILE_CARD_DETAILS.replace(':id', id);
        }
        // For no ID (or when ID starts with 'USER'), use the myProfile endpoint
        return API_CONSTANTS.BROKER.GET_BROKER_PROFILE_CARD_DETAILS.replace('/:id', '');
      },
      providesTags: ['Prospect'],
    }),

    // Get broker basic information
    getBrokerBasic: builder.query({
      query: (id) => {
        if (id) {
          return API_CONSTANTS.BROKER.GET_BROKER_BASIC.replace(':id', id);
        }
        return API_CONSTANTS.BROKER.GET_BROKER_BASIC.replace('/:id', '');
      },
      providesTags: ['Prospect'],
    }),

    // Update broker basic information
    updateBrokerBasic: builder.mutation({
      query: ({ brokerId, ...basicData }) => {
        const url = brokerId ? API_CONSTANTS.BROKER.UPDATE_BROKER_BASIC.replace(':id', brokerId) : API_CONSTANTS.BROKER.UPDATE_BROKER_BASIC.replace('/:id', '');
        return {
          url,
          method: 'PUT',
          body: basicData,
        };
      },
      invalidatesTags: ['Prospect'],
    }),

    // Get broker license information
    getBrokerLicense: builder.query({
      query: (id) => {
        if (id) {
          return API_CONSTANTS.BROKER.GET_BROKER_LICENSE.replace(':id', id);
        }
        return API_CONSTANTS.BROKER.GET_BROKER_LICENSE.replace('/:id', '');
      },
      providesTags: ['Prospect'],
    }),

    // Update broker license information
    updateBrokerLicense: builder.mutation({
      query: ({ brokerId, ...licenseData }) => {
        const url = brokerId ? API_CONSTANTS.BROKER.UPDATE_BROKER_LICENSE.replace(':id', brokerId) : API_CONSTANTS.BROKER.UPDATE_BROKER_LICENSE.replace('/:id', '');
        return {
          url,
          method: 'PUT',
          body: licenseData,
        };
      },
      invalidatesTags: ['Prospect'],
    }),

    createReferral: builder.mutation({
      query: (referralData) => ({
        url: API_CONSTANTS.BROKER.CREATE_REFERRAL,
        method: 'POST',
        body: referralData,
      }),
    }),
    getBrokerDetailsById: builder.query({
      query: (id) => {
        if (id) {
          return API_CONSTANTS.BROKER.GET_BROKER_DETAILS_BY_ID.replace(':id', id);
        }
        return API_CONSTANTS.BROKER.GET_BROKER_DETAILS_BY_ID.replace('/:id', '');
      },
    }),
    deleteBroker: builder.mutation({
      query: (brokerId) => ({
        url: API_CONSTANTS.BROKER.DELETE_BROKER,
        method: 'DELETE',
        params: brokerId ? { brokerId } : undefined,
      }),
    }),
    adminDeleteBroker: builder.mutation({
      query: (brokerId) => ({
        url: API_CONSTANTS.ADMIN.ADMIN_DELETE_BROKER.replace(':brokerId', brokerId),
        method: 'DELETE',
      }),
    }),
    getBrokerDashboardStats: builder.query({
      query: ({ brokerId, firmId }) => {
        if (brokerId) {
          return {
            url: API_CONSTANTS.BROKER.GET_BROKER_DASHBOARD_STATS.replace(':brokerId', brokerId),
            method: 'GET',
            params: firmId ? { firmId } : undefined,
          };
        }
      },
    }),
    getTopBrokers: builder.query({
      query: (firmId) => {
        return {
          url: API_CONSTANTS.BROKER.GET_TOP_BROKER,
          method: 'GET',
          params: firmId ? { firmId } : undefined,
        };
      },
    }),
    getBrokerDealConversionDetails: builder.query({
      query: ({ brokerId, timeSpan, limit, firmId }) => {
        if (!brokerId) return '';
        return {
          url: API_CONSTANTS.BROKER.GET_BROKER_DEALS_CONVERSION_DETAILS.replace(':brokerId', brokerId),
          method: 'GET',
          params: { timeSpan, limit, ...(firmId && { firmId }) },
        };
      },
    }),
    getMonthWiseBrokerDeals: builder.query({
      query: ({ brokerId, firmId }) => {
        if (brokerId) {
          return {
            url: API_CONSTANTS.BROKER.GET_MONTHWISE_BROKER_DEALS.replace(':brokerId', brokerId),
            method: 'GET',
            params: firmId ? { firmId } : undefined,
          };
        }
      },
    }),
    createPaymentUrl: builder.mutation({
      query: () => ({
        url: API_CONSTANTS.PAYMENT_URL,
        method: 'GET',
      }),
    }),
    getBrokerToDos: builder.query({
      query: () => API_CONSTANTS.BROKER.GET_BROKER_TODOS,
    }),
    saveRating: builder.mutation({
      query: ({ ratingData }) => ({
        url: API_CONSTANTS.BROKER.SAVE_RATING,
        method: 'POST',
        body: ratingData,
      }),
    }),
    fetchMandateStatus: builder.query({
      query: ({ propertyId, suppressToast = true }) => ({
        url: `/e-sign/fetch-status`,
        method: 'GET',
        params: { propertyId },
        suppressToast,
      }),
    }),
    getAssociatedFirm: builder.query({
      query: (brokerId) => ({
        url: API_CONSTANTS.GET_ASSOCIATED_FIRM.replace(':brokerId', brokerId),
        method: 'GET',
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: (result, error, brokerId) => [{ type: 'AssociatedFirm', id: brokerId }],
    }),
    assignFirmToBroker: builder.mutation({
      query: ({ brokerId, firmId }) => ({
        url: `/broker/${brokerId}/firm/${firmId}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, { brokerId }) => [{ type: 'AssociatedFirm', id: brokerId }],
    }),
  }),
});

export const {
  useGetDocumentTypesQuery,
  useGetDocumentsQuery,
  useGetBrokersQuery,
  useCreateProspectMutation,
  useUploadProfileImageMutation,
  useCreateProspectLicenseMutation,
  useAddDocumentMutation,
  useGetBrokerStatsQuery,
  useGetSampleDocumentsQuery,
  useDownloadSampleDocumentsMutation,
  useDeleteDocumentMutation,
  useGetDocumentByIdQuery,
  useUpdateDocumentMutation,
  useGetBrokerDetailsQuery,
  useGetBrokerProfileCardDetailsQuery,
  useGetBrokerBasicQuery,
  useUpdateBrokerBasicMutation,
  useGetBrokerLicenseQuery,
  useUpdateBrokerLicenseMutation,
  useCreateReferralMutation,
  useGetBrokerDetailsByIdQuery,
  useDeleteBrokerMutation,
  useAdminDeleteBrokerMutation,
  useGetBrokerDashboardStatsQuery,
  useGetTopBrokersQuery,
  useCreatePaymentUrlMutation,
  useGetBrokerDealConversionDetailsQuery,
  useGetMonthWiseBrokerDealsQuery,
  useGetBrokerToDosQuery,
  useSaveRatingMutation,
  useFetchMandateStatusQuery,
  useGetAssociatedFirmQuery,
  useAssignFirmToBrokerMutation,
} = brokerApi;
